import { debounce } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import type { IPrivacyPolicy, IStation } from '../../../models/search-page';
import { getConfigurationDetail } from '../../../services/common/common-service';
import { getCodesList } from '../../../services/search-page/search-page-service';
import type { ICommonProps } from '../../../types/propTypes';
import { Common } from '../../../utils/constants';

export const useSearchPage = (props: ICommonProps) => {
    const { t } = props;

    const navigate = useNavigate();
    const [showQRScanner, setShowQRScanner] = useState(false);
    const [privacyPolicy, setPrivacyPolicy] = useState<IPrivacyPolicy | null>(null);

    useEffect(() => {
        const fetchPrivacyPolicy = async () => {
            try {
                const res = await getConfigurationDetail({
                    code: Common.ConfigurationKey.PRIVACY_POLICY,
                });

                if (!(res && res.Data)) {
                    console.error(t('searchPage.label.privacyPolicyNotFound'));
                    return;
                }

                const privacyPolicyConfig = res.Data.find(
                    (config) => config.reference_code === Common.ConfigurationKey.PRIVACY_POLICY,
                );

                if (!privacyPolicyConfig?.value) {
                    console.error(t('searchPage.label.privacyPolicyNotFound'));
                    return;
                }

                const policies = JSON.parse(privacyPolicyConfig.value);
                const browserLanguage = navigator.language.split('-')[0]; // Get the browser's default language (e.g., "en", "es")

                const getPolicyByLanguage = (language) =>
                    policies.find((policy) => policy.code === language && policy.web_pay_policy_url && policy.title);

                const selectedPolicy = getPolicyByLanguage(browserLanguage) || getPolicyByLanguage('en');

                setPrivacyPolicy(selectedPolicy || null);
            } catch (error) {
                if (error instanceof SyntaxError) {
                    console.error(t('searchPage.label.errorParsingJSON'), error);
                } else {
                    console.error('error', error);
                }
            }
        };

        fetchPrivacyPolicy();
    }, []);

    const openQRCodeScanner = () => {
        setShowQRScanner(true);
    };

    const handleSelect = (value) => {
        if (value && value.unique_code) {
            navigate(`/charge-station/${value.unique_code}`);
        }
    };

    const handleQRScan = (result) => {
        if (result && result.url) {
            if (validateUrl(result.url)) {
                setShowQRScanner(false);
                navigateToUrl(`${result.url}`);
            } else {
                toast.error('Invalid QR Code');
            }
        } else if (result && result.errorName && result.errorMessage) {
            toast.error(result.errorMessage);
        } else {
            toast.error('Invalid QR Code');
        }
        setShowQRScanner(false);
    };

    const navigateToUrl = (url) => {
        if (!(url.startsWith('http://') || url.startsWith('https://'))) {
            url = `http://${url}`;
        }
        window.location.href = url;
    };

    const validateUrl = (url) => {
        const urlRegex = new RegExp(/(?:^|[ \t])((https?:\/\/)?(?:localhost|[\w-]+(?:\.[\w-]+)+)(:\d+)?(\/\S*)?)/gm);
        return urlRegex.test(url);
    };

    const loadOptions = React.useRef(
        debounce((code, callback) => {
            getAsyncOptions(code).then((options) => callback(options));
        }, 500),
    ).current;

    const getAsyncOptions = async (code) => {
        let codeList: IStation[] = [];

        const filterInput = {
            unique_code: code,
            limit: 10,
        };
        await getCodesList(filterInput)
            .then((res) => {
                codeList = res.Data;
                if (codeList && codeList.length >= 10) {
                    const messageOption = {
                        unique_code: 'Continue typing to rectify search',
                        isdisabled: true,
                    };
                    codeList.push(messageOption as IStation);
                }
            })
            .catch((m) => {
                if (m.response && m.response.data && m.response.data.message) {
                    toast.error(m.response.data.message);
                } else {
                    toast.error(m.toString());
                }
            });

        return codeList;
    };

    React.useEffect(() => {
        return () => {
            loadOptions.cancel();
            localStorage.removeItem('tryAgainCount');
            localStorage.removeItem('payWithDifferentCardCount');
        };
    }, [loadOptions]);

    return {
        t,
        showQRScanner,
        privacyPolicy,
        loadOptions,
        openQRCodeScanner,
        handleSelect,
        handleQRScan,
        navigateToUrl,
        validateUrl,
        getAsyncOptions,
    };
};
